import { useDocumentAssets } from "@/composables/documentAssets";
import { useBookingsStore } from "@/store/bookings";
import { useUserStore } from "@/store/user";
import {
  BookingOrdersApi,
  DocumentAssetsApi,
  MarketplaceApi,
  RelatedTypes,
  useApiListQuery,
  useBookingorderMemos,
  useBookingorders,
  type BookingOrderSimpleResponse,
  type DocumentAssetDto,
  type LogisticStatus,
  type MemoResponse,
  type TicketResponseMarketplace,
} from "@xs2event/js-lib/api";
import { camelToSnake, defineContext, t, useFilters, utcToApiDate } from "@xs2event/js-lib/util";
import { computed, ref, watch } from "vue";
import { useToast } from "@xs2event/js-lib/toast";
import { useRoute, useRouter } from "vue-router";

export type BookingorderSection = "payment";

const [provideBookingContext, useBookingContext] = defineContext(() => {
  const bookingsStore = useBookingsStore();

  const filters = useFilters({
    query: { default: "" },
    date: {
      default: undefined as [string, string] | string | undefined,
      serialize: (x) => {
        if (typeof x != "string") return undefined;

        if (x.includes(",")) {
          return x.split(",").join(",");
        }
        return x;
      },
      deserialize: (x) => {
        if (typeof x == "string" && x.includes(",")) {
          return x.split(",");
        }
        return x?.split(",");
      },
    },
    eventId: { default: undefined as string | undefined },
    logisticStatus: { default: undefined as LogisticStatus | undefined },
    futureEvents: { default: 0 as 0 | 1 },
    page: { default: 1 },
    pageSize: { default: 25 },
    sorting: { default: "created:desc" },
  });

  const route = useRoute();
  const router = useRouter();
  const { addToast } = useToast();

  const selectedBookingorder = ref<Bookingorder>();

  // The opened bookingorder lives in the url (/user/bookings?booking=<id>&section=payment), so it can be linked to directly
  const openBookingorder = (row: Bookingorder, section?: BookingorderSection) => {
    selectedBookingorder.value = row;
    router.push({ query: { ...route.query, booking: row.bookingorderId, section } });
  };

  const openBookingorderMemos = (row: Bookingorder) => {
    openBookingorder(row);
    bookingsStore.openTab(4);
  };

  const closeBookingorder = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { booking, section, ...query } = route.query;
    router.replace({ query });
  };

  const notFound = () => {
    addToast({ title: t("Error"), content: t("This booking could not be found."), duration: 7000 });
    closeBookingorder();
  };

  const isCurrentRoute = (id: string) => route.query.booking === id;

  watch(
    () => route.query.booking as string | undefined,
    async (id) => {
      if (!id) {
        bookingsStore.bookingorder = null;
        bookingsStore.conditions = null;
        bookingsStore.openTab(0);
        selectedBookingorder.value = undefined;
        return;
      }

      if (bookingsStore.bookingorder?.bookingorder_id === id) return;

      try {
        if (selectedBookingorder.value?.bookingorderId !== id) {
          const row = await fetchOverviewBookingorder(id);
          if (!isCurrentRoute(id)) return;
          if (!row) return notFound();
          selectedBookingorder.value = row;
        }

        await Promise.all([bookingsStore.fetchBookingOrder(id), bookingsStore.getMemosByBookingorderIds([id])]);
      } catch {
        // Unknown bookingorder or no access: fall back to the overview
        if (isCurrentRoute(id)) notFound();
      }
    },
    { immediate: true }
  );

  return { filters, openBookingorder, openBookingorderMemos, closeBookingorder, selectedBookingorder };
});

const toOverviewBookingorder = (
  bookingorder: BookingOrderSimpleResponse,
  documentAssets: DocumentAssetDto[],
  memos: MemoResponse[],
  listing?: TicketResponseMarketplace
) => ({
  ...bookingorder,
  documentAssets,
  memos,
  quantity: bookingorder.items?.reduce((sum, item) => sum + (item.quantity || 0), 0),
  listing: listing && listing.stock ? listing : undefined,
  ticketName: bookingorder.items?.length ? bookingorder.items?.map((item) => item.ticketName).join(", ") : t("Various"),
});

// Builds the same row the overview table produces, for a bookingorder opened directly by url
const fetchOverviewBookingorder = async (bookingorderId: string) => {
  const [{ bookingorders }, { documentassets }] = await Promise.all([
    new BookingOrdersApi().bookingorderslistGet({ bookingorderId }),
    new DocumentAssetsApi().documentassetslistGet({
      relatedType: RelatedTypes.Bookingorder,
      relatedId: bookingorderId,
    }),
  ]);

  const bookingorder = bookingorders.find((bo) => bo.bookingorderId === bookingorderId);
  return bookingorder && toOverviewBookingorder(bookingorder, documentassets, []);
};

export type Bookingorder = ReturnType<typeof toOverviewBookingorder>;

export const useOverviewBookingorders = () => {
  const { filters } = useBookingContext();

  const route = useRoute();

  const userStore = useUserStore();

  const { query, eventId, date, logisticStatus, page, pageSize, sorting } = filters;

  const bookingordersQuery = useBookingorders(
    computed(() => ({
      query: query.value?.trim() ? `ilike:${query.value.trim()}` : undefined,
      eventId: eventId.value,
      eventStartdate: date.value?.[0] && `ge:${utcToApiDate(date.value[0])}`,
      eventStopdate: date.value?.[1] && `le:${utcToApiDate(date.value[1])}`,
      logisticStatus: logisticStatus.value,
    })),
    {
      pageSize: pageSize,
      page: page,
      sorting: computed(() => camelToSnake(sorting.value || "created:desc")),

      enabled: computed(() => (userStore.isAdmin && !!Object.keys(route.query).length) || !userStore.isAdmin),
    }
  );

  // Fetch Assets for each booking order
  const documentAssetsQuery = useDocumentAssets(
    computed(() => ({
      relatedType: "bookingorder",
      bookingorderId: `in:${Array.from(bookingordersQuery.data.value || [])
        .map((bo) => bo.bookingorderId)
        .filter(Boolean)
        .join(",")}`,
    })),
    { enabled: computed(() => !!bookingordersQuery.data.value.length) }
  );

  const bookingorderMemosQuery = useBookingorderMemos(
    computed(() => Array.from(new Set(bookingordersQuery.data.value?.map((bo) => bo.bookingorderId!))))
  );

  const marketplaceApi = new MarketplaceApi();

  const relistedTicketsQuery = useApiListQuery(
    marketplaceApi,
    "marktplaceticketsGet",
    {
      isRelistedTicket: "true",
      ticketStatus: "in:[available,disabled]",
      supplierId: userStore.supplier_id,
    },
    { pageSize: "all", enabled: computed(() => userStore.isAdmin || userStore.isSupplier) }
  );

  const isLoading = computed(
    () =>
      documentAssetsQuery.isFetching.value ||
      bookingordersQuery.isFetching.value ||
      bookingorderMemosQuery.isFetching.value ||
      relistedTicketsQuery.isFetching.value
  );

  const data = computed(() => {
    if (isLoading.value) return [];

    return bookingordersQuery.data.value?.map((bookingorder) => {
      const documentAssets =
        documentAssetsQuery.data.value?.filter((asset) => asset.relatedId === bookingorder.bookingorderId) || [];

      const memos =
        bookingorderMemosQuery.data.value?.filter((memo) => memo.bookingorderId === bookingorder.bookingorderId) || [];

      const listing = relistedTicketsQuery.data.value?.find((ticket) =>
        ticket.externalTicketId?.includes(bookingorder.bookingorderId!)
      );

      return toOverviewBookingorder(bookingorder, documentAssets, memos, listing);
    });
  });

  return { data, isLoading, pages: bookingordersQuery.pages };
};

export { provideBookingContext, useBookingContext };
